import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Building2, FileText, Palette, Award, BookOpen, ShieldCheck, XCircle } from "lucide-react";

const STEPS = [
  { key: "organization", label: "Organization", icon: Building2 },
  { key: "documents",    label: "Documents",    icon: FileText },
  { key: "branding",     label: "Branding",     icon: Palette },
  { key: "accreditations", label: "Accreditation", icon: Award },
  { key: "first_course", label: "First Course", icon: BookOpen, optional: true },
  { key: "agreement",    label: "Submit",       icon: ShieldCheck },
];

const DOC_KINDS = [
  { key: "registration", label: "Company Registration" },
  { key: "business_licence", label: "Business Licence" },
  { key: "accreditation", label: "Accreditation Certificate" },
  { key: "approval", label: "Approval / Certification" },
  { key: "insurance", label: "Insurance" },
  { key: "other", label: "Other" },
];

export default function PartnerOnboarding() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/api/partner-onboarding/state").then(({ data }) => setState(data))
       .catch((e) => {
         if (e?.response?.status === 403) {
           toast.error("Only partner accounts can access this wizard");
           nav("/");
         }
       });

  useEffect(() => {
    if (!user) return nav("/login");
    if (user.role !== "partner") return nav("/dashboard");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!state) return <div className="p-12 text-center">Loading your onboarding…</div>;

  const partner = state.partner;
  const progress = state.progress;
  const done = STEPS.filter((s) => progress[s.key] || s.optional).length;
  const pct = Math.round((done / STEPS.length) * 100);

  // If already applied/approved, don't force wizard
  if (["applied", "under_review", "approved"].includes(partner.status) && step === 0 && !partner.changes_requested_note) {
    // still allow re-entry — but nudge them to the dashboard
  }

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goPrev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="partner-onboarding-root">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#DC2626] font-semibold">Training Partner</div>
              <h1 className="font-display font-black text-2xl">Complete your partner profile</h1>
            </div>
            <div className="text-right">
              <div className="text-xs text-[#6B7280]">{done}/{STEPS.length} steps · {pct}%</div>
              <Badge className={`mt-1 capitalize ${
                partner.status === "approved" ? "bg-emerald-100 text-emerald-800"
                : partner.status === "changes_requested" ? "bg-amber-100 text-amber-800"
                : partner.status === "applied" || partner.status === "under_review" ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-700"}`}>
                {partner.status.replace("_", " ")}
              </Badge>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#DC2626] transition-all" style={{ width: `${pct}%` }} />
          </div>

          {/* Changes-requested banner */}
          {partner.changes_requested_note && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="onboarding-changes-banner">
              <div className="font-semibold text-amber-900 text-sm">Admin has requested changes</div>
              <div className="text-sm text-amber-800 mt-1 whitespace-pre-line">{partner.changes_requested_note}</div>
            </div>
          )}
          {partner.status === "applied" || partner.status === "under_review" ? (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900" data-testid="onboarding-review-banner">
              Your Training Partner application is currently under review. We'll email you within 3 business days.
            </div>
          ) : null}
        </div>
      </div>

      <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-6 px-6 py-8">
        {/* Sidebar step list */}
        <div className="md:col-span-1" data-testid="onboarding-stepper">
          <div className="sticky top-6 space-y-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = progress[s.key];
              return (
                <button key={s.key} onClick={() => setStep(i)}
                  data-testid={`onboarding-step-${s.key}`}
                  className={`w-full text-left flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive ? "bg-white shadow-sm border border-[#E5E7EB]"
                             : "hover:bg-white/60"}`}>
                  <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold shrink-0 ${
                    isDone ? "bg-emerald-500 text-white"
                           : isActive ? "bg-[#DC2626] text-white" : "bg-gray-200 text-gray-600"}`}>
                    {isDone ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold text-xs ${isActive ? "text-[#111827]" : "text-[#4B5563]"}`}>
                      {s.label}
                    </div>
                    {s.optional && <div className="text-[10px] text-gray-500">optional</div>}
                  </div>
                  <Icon className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
            <button onClick={logout}
              data-testid="onboarding-signout"
              className="w-full text-left text-xs text-gray-500 hover:text-red-600 mt-6 px-3">
              Sign out
            </button>
          </div>
        </div>

        {/* Step content */}
        <div className="md:col-span-3">
          <Card>
            <CardContent className="p-6 md:p-8">
              {step === 0 && <OrgStep state={state} refresh={load} busy={busy} setBusy={setBusy} onNext={goNext} />}
              {step === 1 && <DocsStep state={state} refresh={load} onNext={goNext} onPrev={goPrev} />}
              {step === 2 && <BrandingStep state={state} refresh={load} onNext={goNext} onPrev={goPrev} />}
              {step === 3 && <AccredStep state={state} refresh={load} onNext={goNext} onPrev={goPrev} />}
              {step === 4 && <FirstCourseStep state={state} refresh={load} onNext={goNext} onPrev={goPrev} nav={nav} />}
              {step === 5 && <AgreementStep state={state} refresh={load} onPrev={goPrev} nav={nav} />}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---------------- Step 1: Organization ----------------
function OrgStep({ state, refresh, busy, setBusy, onNext }) {
  const p = state.partner;
  const [form, setForm] = useState({
    organization_name: p.organization_name || "",
    legal_name: p.legal_name || "",
    country: p.country || "Canada",
    province: p.province || "",
    address: p.address || "",
    website: p.website || "",
    contact_phone: p.contact_phone || "",
    contact_name: p.contact_name || "",
    about: p.about || "",
    training_categories: (p.training_categories || []).join(", "),
  });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/api/partner-onboarding/organization", form);
      toast.success("Saved");
      await refresh();
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="onboarding-org-form">
      <h2 className="font-display font-bold text-xl">Step 1 · Organization</h2>
      <p className="text-sm text-gray-500 -mt-1">Tell us who's behind the training. We use this on your storefront and certificates.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Organization / Trading name *" testid="org-name">
          <Input required value={form.organization_name} onChange={set("organization_name")} data-testid="org-name-input" />
        </Field>
        <Field label="Legal company name" testid="org-legal">
          <Input value={form.legal_name} onChange={set("legal_name")} data-testid="org-legal-input" />
        </Field>
        <Field label="Country">
          <Input value={form.country} onChange={set("country")} />
        </Field>
        <Field label="Province / State">
          <Input value={form.province} onChange={set("province")} />
        </Field>
        <Field label="Address" wide>
          <Input value={form.address} onChange={set("address")} />
        </Field>
        <Field label="Website">
          <Input placeholder="https://" value={form.website} onChange={set("website")} />
        </Field>
        <Field label="Phone">
          <Input value={form.contact_phone} onChange={set("contact_phone")} />
        </Field>
        <Field label="Primary contact name *">
          <Input required value={form.contact_name} onChange={set("contact_name")} />
        </Field>
        <Field label="Organization description (200-1000 chars)" wide>
          <Textarea rows={4} value={form.about} onChange={set("about")} data-testid="org-about-input" />
        </Field>
        <Field label="Training categories (comma-separated)" wide>
          <Input placeholder="e.g. Health & Safety, Continuing Ed, Engineering" value={form.training_categories} onChange={set("training_categories")} />
        </Field>
      </div>
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={busy} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="org-save-btn">
          {busy ? "Saving…" : "Save & continue →"}
        </Button>
      </div>
    </form>
  );
}

const Field = ({ label, wide, children, testid }) => (
  <div className={wide ? "md:col-span-2" : ""} data-testid={testid ? `field-${testid}` : undefined}>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

// ---------------- Step 2: Documents ----------------
function DocsStep({ state, refresh, onNext, onPrev }) {
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("registration");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);

  const upload = async () => {
    if (!file) return toast.error("Pick a file first");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      if (label) form.append("label", label);
      await api.post("/api/partner-onboarding/documents", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setFile(null); setLabel("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/api/partner-onboarding/documents/${id}`);
    await refresh();
  };

  return (
    <div className="space-y-5" data-testid="onboarding-docs-form">
      <h2 className="font-display font-bold text-xl">Step 2 · Organization documents</h2>
      <p className="text-sm text-gray-500 -mt-1">Upload any of the following that apply. No single document is universally required — admin will tell you if something's missing.</p>

      <Card className="bg-[#F7F5F0] border-none">
        <CardContent className="p-4 space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Document type" testid="doc-kind">
              <select value={kind} onChange={(e) => setKind(e.target.value)}
                data-testid="doc-kind-select"
                className="w-full h-10 px-3 rounded-md border text-sm">
                {DOC_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </Field>
            <Field label="Custom label (optional)" testid="doc-label">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="doc-label-input" />
            </Field>
            <Field label="File (PDF / image, ≤ 30 MB)" testid="doc-file">
              <Input type="file" accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0])}
                data-testid="doc-file-input" />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button onClick={upload} disabled={uploading || !file}
              data-testid="doc-upload-btn"
              className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {uploading ? "Uploading…" : "Upload document"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {state.documents.length === 0 ? (
        <div className="text-sm text-gray-500" data-testid="onboarding-docs-empty">No documents uploaded yet.</div>
      ) : (
        <div className="space-y-2" data-testid="onboarding-docs-list">
          {state.documents.map((d) => (
            <div key={d.id} className="flex items-center gap-3 bg-white border border-[#E5E7EB] rounded-md p-3"
              data-testid="onboarding-doc-item">
              <FileText className="w-5 h-5 text-[#DC2626] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{d.label || d.filename}</div>
                <div className="text-xs text-gray-500">{d.kind.replace("_", " ")} · {d.size_mb} MB</div>
              </div>
              <button onClick={() => del(d.id)} data-testid="onboarding-doc-del"
                className="text-xs text-red-600 hover:underline">Delete</button>
            </div>
          ))}
        </div>
      )}

      <StepNav onPrev={onPrev} onNext={onNext} />
    </div>
  );
}

// ---------------- Step 3: Branding ----------------
function BrandingStep({ state, refresh, onNext, onPrev }) {
  const p = state.partner;
  const [form, setForm] = useState({
    logo_url: p.logo_url || "",
    cover_image_url: p.cover_image_url || "",
    signature_image_url: p.signature_image_url || "",
    seal_image_url: p.seal_image_url || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/api/partner-onboarding/branding", form);
      toast.success("Saved");
      await refresh();
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="onboarding-branding-form">
      <h2 className="font-display font-bold text-xl">Step 3 · Branding</h2>
      <p className="text-sm text-gray-500 -mt-1">These URLs appear on your storefront and certificate PDFs. Upload via the Media page or paste any hosted URL.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <BrandingField label="Company logo URL" value={form.logo_url} onChange={set("logo_url")} testid="brand-logo" />
        <BrandingField label="Cover / banner image URL" value={form.cover_image_url} onChange={set("cover_image_url")} testid="brand-cover" />
        <BrandingField label="Authorized signature image URL" value={form.signature_image_url} onChange={set("signature_image_url")} testid="brand-signature" />
        <BrandingField label="Organization seal URL (optional)" value={form.seal_image_url} onChange={set("seal_image_url")} testid="brand-seal" />
      </div>
      <StepNav onPrev={onPrev} onNext={save} nextLabel={busy ? "Saving…" : "Save & continue →"} disabled={busy} />
    </div>
  );
}

function BrandingField({ label, value, onChange, testid }) {
  return (
    <div data-testid={`field-${testid}`}>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={onChange} placeholder="https://..." data-testid={`${testid}-input`} className="mt-1" />
      {value && (
        <div className="mt-2 aspect-video bg-white border border-dashed border-[#E5E7EB] rounded overflow-hidden">
          <img src={value} alt={label} className="w-full h-full object-contain"
            onError={(e) => { e.target.style.opacity = 0.2; }} />
        </div>
      )}
    </div>
  );
}

// ---------------- Step 4: Accreditation ----------------
function AccredStep({ state, refresh, onNext, onPrev }) {
  const [form, setForm] = useState({
    accrediting_organization: "", accreditation_name: "",
    accreditation_number: "", valid_from: "", valid_until: "",
    verification_url: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const add = async () => {
    if (!form.accrediting_organization.trim()) return toast.error("Enter the accrediting organization");
    setBusy(true);
    try {
      await api.post("/api/partner-onboarding/accreditations", form);
      toast.success("Accreditation added");
      setForm({ accrediting_organization: "", accreditation_name: "", accreditation_number: "",
        valid_from: "", valid_until: "", verification_url: "" });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this accreditation?")) return;
    await api.delete(`/api/partner-onboarding/accreditations/${id}`);
    await refresh();
  };

  return (
    <div className="space-y-5" data-testid="onboarding-accred-form">
      <h2 className="font-display font-bold text-xl">Step 4 · Accreditation</h2>
      <p className="text-sm text-gray-500 -mt-1">Each accreditation ships as <b>PENDING VERIFICATION</b> until admin marks it VERIFIED.</p>

      <Card className="bg-[#F7F5F0] border-none">
        <CardContent className="p-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Accrediting organization *"><Input value={form.accrediting_organization} onChange={set("accrediting_organization")} data-testid="accred-org-input" /></Field>
            <Field label="Accreditation name"><Input value={form.accreditation_name} onChange={set("accreditation_name")} /></Field>
            <Field label="Accreditation number"><Input value={form.accreditation_number} onChange={set("accreditation_number")} /></Field>
            <Field label="Verification URL"><Input value={form.verification_url} onChange={set("verification_url")} placeholder="https://" /></Field>
            <Field label="Valid from"><Input type="date" value={form.valid_from} onChange={set("valid_from")} /></Field>
            <Field label="Valid until"><Input type="date" value={form.valid_until} onChange={set("valid_until")} /></Field>
          </div>
          <div className="flex justify-end">
            <Button onClick={add} disabled={busy} data-testid="accred-add-btn" className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {busy ? "Adding…" : "Add accreditation"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {state.accreditations.length === 0 ? (
        <div className="text-sm text-gray-500" data-testid="onboarding-accreds-empty">No accreditations added yet.</div>
      ) : (
        <div className="space-y-2" data-testid="onboarding-accreds-list">
          {state.accreditations.map((a) => (
            <div key={a.id} className="flex items-center gap-3 bg-white border border-[#E5E7EB] rounded-md p-3">
              <Award className="w-5 h-5 text-[#F59E0B] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{a.accrediting_organization}</div>
                <div className="text-xs text-gray-500">
                  {a.accreditation_name || "—"}
                  {a.accreditation_number && ` · #${a.accreditation_number}`}
                </div>
              </div>
              <Badge className={a.verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                {a.verified ? "Verified" : "Pending verification"}
              </Badge>
              <button onClick={() => del(a.id)} className="text-xs text-red-600 hover:underline ml-2">Delete</button>
            </div>
          ))}
        </div>
      )}

      <StepNav onPrev={onPrev} onNext={onNext} />
    </div>
  );
}

// ---------------- Step 5: First Course (optional) ----------------
function FirstCourseStep({ state, refresh, onNext, onPrev, nav }) {
  const [form, setForm] = useState({
    title: "", slug: "", short_description: "", description: "",
    price_usd: 0, level: "Beginner",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const add = async () => {
    if (!form.title || !form.slug) return toast.error("Title and slug required");
    setBusy(true);
    try {
      const payload = { ...form, price_usd: Number(form.price_usd) };
      const { data } = await api.post("/api/partner/courses", payload);
      toast.success("Draft course created");
      await refresh();
      // Jump into the course editor
      nav(`/partner/courses/${data.id}/edit`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="onboarding-firstcourse-form">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="font-display font-bold text-xl">Step 5 · Your first course</h2>
        <Badge className="bg-blue-100 text-blue-800 text-xs">Optional</Badge>
      </div>
      <p className="text-sm text-gray-500 -mt-1">
        Sketch out one course now (title + short description is enough) — you can add modules, videos,
        quizzes, and cohorts later from your dashboard. Or skip this step and come back after admin approval.
      </p>

      {state.course_count > 0 ? (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-900"
          data-testid="onboarding-firstcourse-done">
          You already have <b>{state.course_count} course{state.course_count === 1 ? "" : "s"}</b> in draft. Add more anytime from /partner/courses.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Course name *"><Input value={form.title} onChange={set("title")} data-testid="fc-title-input" /></Field>
          <Field label="URL slug *"><Input value={form.slug} onChange={set("slug")} placeholder="e.g. wsib-basics" data-testid="fc-slug-input" /></Field>
          <Field label="Short description" wide><Input value={form.short_description} onChange={set("short_description")} data-testid="fc-short-input" /></Field>
          <Field label="Full description" wide><Textarea rows={3} value={form.description} onChange={set("description")} /></Field>
          <Field label="Price (USD)"><Input type="number" value={form.price_usd} onChange={set("price_usd")} /></Field>
          <Field label="Level">
            <select value={form.level} onChange={set("level")} className="w-full h-10 px-3 rounded-md border text-sm">
              <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
            </select>
          </Field>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onPrev} data-testid="fc-prev-btn">← Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onNext} data-testid="fc-skip-btn">Skip for now →</Button>
          {state.course_count === 0 && (
            <Button onClick={add} disabled={busy} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="fc-add-btn">
              {busy ? "Creating…" : "Add first course"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Step 6: Agreement + Submit ----------------
const AGREEMENT_LINES = [
  "Your organization will publish courses on Teacher4Me under a dual-brand certificate.",
  "You are responsible for the accuracy and legality of your course content.",
  "Content flagged by our compliance system requires admin review before publish.",
  "Storage is metered — 500 MB starter quota per partner.",
  "Revenue share is tracked manually; payouts subject to a separate written agreement.",
  "Teacher4Me may suspend or reject partners at any time for non-compliance with Canadian law.",
  "You have not been debarred by any professional regulator in the past 5 years.",
];

function AgreementStep({ state, refresh, onPrev, nav }) {
  const p = state.partner;
  const [accept, setAccept] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!accept) return toast.error("Please accept the agreement");
    setBusy(true);
    try {
      await api.post("/api/partner-onboarding/submit", { accept_agreement: true, signature });
      toast.success("Application submitted — we'll review within 3 business days.");
      await refresh();
      nav("/partner/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const alreadyApplied = ["applied", "under_review", "approved"].includes(p.status);

  return (
    <div className="space-y-4" data-testid="onboarding-agreement-form">
      <h2 className="font-display font-bold text-xl">Step 6 · Agreement & submit</h2>

      {alreadyApplied ? (
        <div className="p-6 bg-blue-50 border border-blue-200 rounded-md text-blue-900" data-testid="onboarding-already-applied">
          <div className="font-bold mb-1">You've already submitted this application 🎉</div>
          <div className="text-sm">Status: <b className="capitalize">{p.status.replace("_", " ")}</b>. You can still keep editing your profile, adding documents, and drafting courses.</div>
          <Button onClick={() => nav("/partner/dashboard")}
            data-testid="onboarding-goto-dashboard"
            className="mt-3 bg-[#DC2626] hover:bg-[#B91C1C]">Go to my dashboard →</Button>
        </div>
      ) : (
        <>
          <div className="bg-[#F7F5F0] border border-[#E5E7EB] rounded-md p-4 space-y-2">
            <div className="font-semibold text-sm">Teacher4Me Training Partner Agreement (v1)</div>
            <div className="text-xs text-gray-500 mb-1">By e-signing below, {p.contact_name} acknowledges that:</div>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              {AGREEMENT_LINES.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)}
              data-testid="agreement-accept-checkbox" className="mt-1" />
            <span className="text-sm">
              I confirm that the information and documents submitted are accurate and that I am
              authorized to represent this organization.
            </span>
          </label>

          <div>
            <Label className="text-xs">Type your full legal name as e-signature *</Label>
            <Input value={signature} onChange={(e) => setSignature(e.target.value)}
              data-testid="agreement-signature-input"
              placeholder={`Must match "${p.contact_name}"`} className="mt-1" />
          </div>

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={onPrev} data-testid="agreement-prev-btn">← Back</Button>
            <Button onClick={submit} disabled={busy || !accept}
              data-testid="agreement-submit-btn"
              className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {busy ? "Submitting…" : "Submit partner application"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function StepNav({ onPrev, onNext, nextLabel = "Continue →", disabled }) {
  return (
    <div className="flex justify-between pt-2">
      <Button variant="outline" onClick={onPrev} data-testid="step-prev-btn">← Back</Button>
      <Button onClick={onNext} disabled={disabled}
        data-testid="step-next-btn"
        className="bg-[#DC2626] hover:bg-[#B91C1C]">
        {nextLabel}
      </Button>
    </div>
  );
}
